import { useCallback, useEffect, useState, Component } from "react";
import { CartProvider, useCart } from "@/context/CartContext";
import { getLenis, initLenis, scrollToId } from "@/lib/scroll";
import { Toaster } from "@/components/ui/sonner";
import Header from "@/components/Header";
import MobileMenu from "@/components/MobileMenu";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import Categories from "@/components/Categories";
import Catalog from "@/components/Catalog";
import Services from "@/components/Services";
import BookingModal from "@/components/BookingModal";
import About from "@/components/About";
import Gallery from "@/components/Gallery";
import Faq from "@/components/Faq";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import WhatsAppFloat from "@/components/WhatsAppFloat";

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    render() {
        return this.state.hasError ? (
            <div className="grid min-h-screen place-items-center bg-cream font-sans text-forest">
                <div className="text-center">
                    <p className="font-serif text-2xl font-bold">Au au... algo deu errado.</p>
                    <button
                        type="button"
                        onClick={() => window.location.reload()}
                        className="mt-4 rounded-full bg-forest px-6 py-3 text-sm font-bold text-cream"
                    >
                        Recarregar página
                    </button>
                </div>
            </div>
        ) : (
            this.props.children
        );
    }
}

function Shell() {
    const { open: cartOpen } = useCart();
    const [menuOpen, setMenuOpen] = useState(false);
    const [activeCategory, setActiveCategory] = useState("todos");
    const [booking, setBooking] = useState({ open: false, serviceId: null });

    const anyOverlay = menuOpen || cartOpen || booking.open;
    useEffect(() => {
        const lenis = getLenis();
        if (!lenis) return;
        if (anyOverlay) {
            lenis.stop();
        } else {
            lenis.start();
        }
    }, [anyOverlay]);

    useEffect(() => {
        const setCategory = (e) => setActiveCategory(e.detail);
        window.addEventListener("mimo:set-category", setCategory);
        return () => window.removeEventListener("mimo:set-category", setCategory);
    }, []);

    const openBooking = useCallback((serviceId = null) => {
        setBooking({ open: true, serviceId });
    }, []);

    const pickCategory = useCallback((catId) => {
        setActiveCategory(catId);
        scrollToId("catalogo");
    }, []);

    return (
        <>
            <Header menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
            <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
            <main>
                <Hero onOpenBooking={() => openBooking(null)} />
                <Marquee />
                <Categories onPick={pickCategory} />
                <Catalog activeCategory={activeCategory} setActiveCategory={setActiveCategory} />
                <Services onSchedule={openBooking} />
                <About />
                <Gallery />
                <Faq />
                <Contact />
            </main>
            <Footer />
            <CartDrawer />
            <BookingModal
                open={booking.open}
                onOpenChange={(open) => setBooking((b) => ({ ...b, open }))}
                initialService={booking.serviceId}
            />
            <WhatsAppFloat />
            <Toaster position="top-center" richColors closeButton />
        </>
    );
}

export default function App() {
    useEffect(() => {
        initLenis();
    }, []);
    return (
        <ErrorBoundary>
            <CartProvider>
                <Shell />
            </CartProvider>
        </ErrorBoundary>
    );
}
